import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Dialog, DialogActions, DialogTitle, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useShouldWarnRemovingUnionStaff } from "../../hooks/useShouldWarnRemovingUnionStaff";
import { INK, NEUTRAL, STATUS, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";

// Functional / Categorical: union-removal coral warning. Must stay stable across themes.
const UNION_WARN_INK = "#eb5834";

import { isOtherReasonLabel } from "../../utils/callIns";
import type { NurseInShift } from "../../utils/models";
import { useCallInReasonOptions } from "../team/CallInReasons/callInReasonsHooks";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../UIToolkit/molecules/InlineHint";

interface IProps {
	nurse: NurseInShift;
	weekIndex: number;
	dayIndex: number;
	shiftNo: number;
	absenceMessage: string;
	setAbsenceMessage: (absenseMessage: string) => void;
	date: DateTime;
	isRemoveButtonEnabled: boolean; // <-- Add this prop
}

/** How far ahead a removal is unusual enough to ask twice. Shared with the gaps-first removal sheet so both ask at the same distance. */
export const REMOVE_NURSE_DAYS_THRESHOLD = 7;

export const ShiftDataNurseRemoval = ({ nurse, weekIndex, dayIndex, shiftNo, absenceMessage, setAbsenceMessage, date, isRemoveButtonEnabled }: IProps) => {
	const { t } = useTranslation();
	const [openRemoveNurseDialog, setOpenRemoveNurseDialog] = useState<boolean>(false);
	const [otherReasonText, setOtherReasonText] = useState<string>("");
	const [removalMessage, setRemovalMessage] = useState<string>("");
	const navigate = useNavigate();
	const { removeNurseFromSpecificDate, showErrorSnackbar, getNurseFormattedName, chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	// This page is scoped to the chosen department (route `shift/:id` under it), so the hook's default
	// list is the right one — unlike the bedside drawer, which acts on a shift that may be elsewhere.
	const { options: reasonDropdownItems, isLoading: isLoadingReasons, isError: isReasonsError } = useCallInReasonOptions();
	const { removeNurse, isLoading } = useHandleNurseChange();
	const shouldWarnRemovingUnionStaff = useShouldWarnRemovingUnionStaff({
		nurseId: nurse.nurseId,
		isRecurringShift: !!nurse.recurringShiftId,
	});

	// The catch-all writes the typed text; every other reason writes its literal label — the same
	// contract as every other removal surface, so what lands in `nurse_in_shift.message` is identical
	// whichever dialog the reason was picked in.
	const getFullReason = (): string => {
		if (!absenceMessage) return "";
		return isOtherReasonLabel(absenceMessage) ? otherReasonText.trim() : absenceMessage;
	};

	// Check if the reason is valid (requires text if the catch-all is selected)
	const isReasonValid = (): boolean => {
		if (!absenceMessage) return false;
		if (isOtherReasonLabel(absenceMessage)) {
			return otherReasonText.trim().length > 0;
		}
		return true;
	};

	const deleteNurse = async (): Promise<void> => {
		try {
			if (isLoading.removeNurse) return;

			const fullReason = getFullReason();

			trackEvent("remove_nurse_mobile", {
				nurse,
			});

			await removeNurse({
				nurse,
				shiftIndex: shiftNo,
				dayNo: dayIndex,
				weekIndex: weekIndex,
				message: fullReason,
				removalMessage: removalMessage || undefined,
			});

			trackEvent("remove_nurse_mobile_success", {
				nurse,
			});

			removeNurseFromSpecificDate(weekIndex!, dayIndex!, shiftNo, nurse.nurseId!, false, nurse.id);
			showSnackbar(
				t("nurse.removed.success", {
					nurseName: nurse?.name,
					day: nurse?.startTime.toFormat("ccc"),
					shiftType: t(nurse?.shiftType),
				}),
				"success",
			);
			const path = `/${chosenDepartment.department_id}/staffing/${nurse.startTime.toFormat("yyyy-MM-dd")}`;
			navigate(path);
		} catch (e) {
			showErrorSnackbar("Failed to remove staff member", { nurseId: nurse.id }, e);
			return;
		}
	};

	const onDeleteClick = (): void => {
		if (date.diff(DateTime.now(), "day").days >= REMOVE_NURSE_DAYS_THRESHOLD) {
			setOpenRemoveNurseDialog(true);
		} else {
			deleteNurse();
		}
	};

	// Compute whether the remove button should be enabled based on validation
	const isRemoveEnabled = isRemoveButtonEnabled && isReasonValid() && !isLoading.removeNurse;

	// Always render the select, but only render the button if enabled
	return (
		<Box
			sx={{
				bgcolor: NEUTRAL[50],
				p: 1.5,
				borderRadius: "4px",
				mb: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexDirection: "column",
				}}
			>
				<Typography
					sx={{
						color: INK.SECONDARY,
						fontWeight: 600,
						mb: 1,
						fontSize: "15px",
						width: "250px",
					}}
				>
					{t("remove.staff.member")}
				</Typography>
				{shouldWarnRemovingUnionStaff && !!absenceMessage && (
					<Typography sx={{ color: UNION_WARN_INK, fontSize: { xs: 12, mediumPhone: 14 } }}>{t("warn.removing.union")}</Typography>
				)}
			</Box>
			<Typography
				sx={{
					color: (theme) => theme.palette.primary.main,
					mb: 0.5,
					fontSize: "13px",
				}}
			>
				{t("choose.reason")}
			</Typography>
			<SystemDropdown
				items={reasonDropdownItems}
				selectedItem={absenceMessage || undefined}
				loading={isLoadingReasons}
				disabled={isLoadingReasons}
				onChange={(e) => {
					setAbsenceMessage(e ?? "");
					setOtherReasonText("");
					trackEvent("remove_nurse_reason_selected", {
						nurseId: nurse.nurseId,
						reason: e ?? "",
					});
				}}
				formControlSx={{
					width: "280px",
				}}
				clearable={true}
				showFilter={false}
				label={""}
				data-testid="shift-data-removal-reason-dropdown"
			/>
			{/* Without this a failed read is an empty picker, which reads as "this department configured
			    no reasons" — and the removal can't proceed, with nothing on screen saying why. */}
			<InlineHint open={isReasonsError} tone="warning" dataTestId="shift-data-removal-reasons-error-hint">
				{t("error.general")}
			</InlineHint>
			{isOtherReasonLabel(absenceMessage) && (
				<Box sx={{ mt: 2 }}>
					<Typography
						sx={{
							color: "primary.main",
							mb: 0.5,
							fontSize: "13px",
						}}
					>
						{t("please.specify.other.reason")}
					</Typography>
					<SystemValidationTextField
						reserveHeightForHelperText={false}
						label={""}
						value={otherReasonText}
						inputSx={{ width: "280px" }}
						onChange={(e) => setOtherReasonText(e.target.value)}
						onClear={() => setOtherReasonText("")}
					/>
				</Box>
			)}
			{absenceMessage && (
				<Box sx={{ mt: 2 }}>
					<Typography
						sx={{
							color: "primary.main",
							mb: 0.5,
							fontSize: "13px",
						}}
					>
						{t("message.optional")}
					</Typography>
					<SystemValidationTextField
						reserveHeightForHelperText={false}
						label={""}
						value={removalMessage}
						inputSx={{ width: "280px" }}
						onChange={(e) => setRemovalMessage(e.target.value)}
						onClear={() => setRemovalMessage("")}
					/>
				</Box>
			)}
			{isRemoveButtonEnabled && (
				<>
					<Button
						fullWidth
						variant="contained"
						onClick={onDeleteClick}
						startIcon={<DeleteIcon />}
						disabled={!isRemoveEnabled}
						sx={{
							bgcolor: STATUS.ERROR.main,
							color: SURFACE,
							textTransform: "none",
							"&:hover": {
								bgcolor: STATUS.ERROR.dark,
							},
							height: "40px",
							fontSize: "14px",
							mt: 2,
						}}
					>
						Remove {getNurseFormattedName(nurse.nurseId)} From {date?.toFormat("MMM d")}
					</Button>
					<Dialog open={openRemoveNurseDialog} onClose={() => setOpenRemoveNurseDialog(false)} maxWidth="sm" fullWidth>
						<DialogTitle sx={{ color: INK.SECONDARY }}>
							Are you sure you want to remove {nurse?.name} from a shift in {date?.diff(DateTime.now(), "day").days} days?
						</DialogTitle>
						<DialogActions>
							<Button onClick={() => setOpenRemoveNurseDialog(false)}>Cancel</Button>
							<Button onClick={deleteNurse} variant="contained" color="primary">
								Remove
							</Button>
						</DialogActions>
					</Dialog>
				</>
			)}
		</Box>
	);
};
